import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { AlertTriangle, Plus, RefreshCw, Shield, Eye, EyeOff, TrendingUp, MapPin, Clock, Users, FileText, Triangle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NearMissReportingPage = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'slip_trip_fall',
    potential_severity: 'minor',
    location: '',
    department: '',
    date_occurred: new Date().toISOString().split('T')[0],
    time_occurred: '',
    witnesses: [],
    contributing_factors: [],
    immediate_actions: '',
    suggested_prevention: '',
    is_anonymous: false
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    fetchCategories();
    fetchFactors();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/near-miss`),
        axios.get(`${API}/near-miss/stats`)
      ]);
      setReports(reportsRes.data.reports || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/near-miss/categories/list`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFactors = async () => {
    try {
      const response = await axios.get(`${API}/near-miss/contributing-factors/suggestions`);
      setFactors(response.data.factors || []);
    } catch (error) {
      console.error('Error fetching factors:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.location) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/near-miss`, formData);
      toast({ title: 'Success', description: 'Near-miss report submitted successfully!' });
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to submit report', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'slip_trip_fall',
      potential_severity: 'minor',
      location: '',
      department: '',
      date_occurred: new Date().toISOString().split('T')[0],
      time_occurred: '',
      witnesses: [],
      contributing_factors: [],
      immediate_actions: '',
      suggested_prevention: '',
      is_anonymous: false
    });
  };

  const toggleFactor = (factor) => {
    const current = formData.contributing_factors;
    if (current.includes(factor)) {
      setFormData({...formData, contributing_factors: current.filter(f => f !== factor)});
    } else {
      setFormData({...formData, contributing_factors: [...current, factor]});
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      minor: 'bg-green-100 text-green-800',
      moderate: 'bg-yellow-100 text-yellow-800',
      serious: 'bg-orange-100 text-orange-800',
      severe: 'bg-red-100 text-red-800',
      fatal: 'bg-purple-100 text-purple-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      action_required: 'bg-orange-100 text-orange-800',
      in_progress: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Near-Miss Reporting</h1>
              <p className="text-sm text-slate-300">Report incidents before they become injuries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" /> Report Near-Miss
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Report Near-Miss Incident</DialogTitle>
                  <DialogDescription>Help prevent future injuries by reporting close calls</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {formData.is_anonymous ? <EyeOff className="w-5 h-5 text-blue-600" /> : <Eye className="w-5 h-5 text-blue-600" />}
                      <span className="text-sm font-medium">Submit Anonymously</span>
                    </div>
                    <Switch checked={formData.is_anonymous} onCheckedChange={(checked) => setFormData({...formData, is_anonymous: checked})} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Title *</Label>
                      <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Brief description of the near-miss" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Potential Severity</Label>
                      <Select value={formData.potential_severity} onValueChange={v => setFormData({...formData, potential_severity: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minor">Minor (First Aid)</SelectItem>
                          <SelectItem value="moderate">Moderate (Medical)</SelectItem>
                          <SelectItem value="serious">Serious (Lost Time)</SelectItem>
                          <SelectItem value="severe">Severe (Disability)</SelectItem>
                          <SelectItem value="fatal">Fatal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Location *</Label>
                      <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="Where did this occur?" />
                    </div>
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Input value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} placeholder="Department" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date Occurred</Label>
                      <Input type="date" value={formData.date_occurred} onChange={e => setFormData({...formData, date_occurred: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input type="time" value={formData.time_occurred} onChange={e => setFormData({...formData, time_occurred: e.target.value})} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Description *</Label>
                      <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Describe what happened in detail" rows={3} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Contributing Factors</Label>
                      <div className="flex flex-wrap gap-2">
                        {factors.map(f => (
                          <Badge key={f} variant={formData.contributing_factors.includes(f) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleFactor(f)}>
                            {f}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Immediate Actions Taken</Label>
                      <Textarea value={formData.immediate_actions} onChange={e => setFormData({...formData, immediate_actions: e.target.value})} placeholder="What was done immediately after?" rows={2} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Suggested Prevention</Label>
                      <Textarea value={formData.suggested_prevention} onChange={e => setFormData({...formData, suggested_prevention: e.target.value})} placeholder="How can we prevent this in the future?" rows={2} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleSubmit}>Submit Report</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Reports</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Last 30 days: {stats.recent_30_days}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Reports</p>
                  <p className="text-3xl font-bold">{(stats.by_status?.new || 0) + (stats.by_status?.under_review || 0)}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Resolved</p>
                  <p className="text-3xl font-bold">{stats.by_status?.resolved || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Reporting Rate</p>
                  <p className="text-3xl font-bold">{stats.heinrich_triangle?.ratio_analysis?.reporting_rate || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Heinrich's Triangle */}
      {stats?.heinrich_triangle && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Triangle className="w-5 h-5" /> Heinrich&apos;s Safety Pyramid
            </CardTitle>
            <CardDescription>The relationship between near-misses and serious incidents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-6">
              <div className="relative">
                <div className="w-0 h-0 border-l-[150px] border-r-[150px] border-b-[250px] border-l-transparent border-r-transparent border-b-gray-200"></div>
                <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center">
                  <p className="font-bold text-red-600 text-lg">{stats.heinrich_triangle.fatalities}</p>
                  <p className="text-xs text-gray-600">Fatalities</p>
                </div>
                <div className="absolute top-16 left-1/2 -translate-x-1/2 text-center">
                  <p className="font-bold text-orange-600 text-xl">{stats.heinrich_triangle.major_injuries}</p>
                  <p className="text-xs text-gray-600">Major Injuries</p>
                </div>
                <div className="absolute top-28 left-1/2 -translate-x-1/2 text-center">
                  <p className="font-bold text-yellow-600 text-2xl">{stats.heinrich_triangle.minor_injuries}</p>
                  <p className="text-xs text-gray-600">Minor Injuries</p>
                </div>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
                  <p className="font-bold text-green-600 text-3xl">{stats.heinrich_triangle.near_misses}</p>
                  <p className="text-sm text-gray-600">Near-Misses</p>
                </div>
              </div>
            </div>
            <div className="text-center text-sm text-gray-600">
              <p>Expected near-misses based on injuries: {stats.heinrich_triangle.ratio_analysis?.expected_near_misses}</p>
              <p className="mt-1">Your reporting rate: <span className="font-bold">{stats.heinrich_triangle.ratio_analysis?.reporting_rate}%</span></p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports List */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Reports</TabsTrigger>
          <TabsTrigger value="new">New ({stats?.by_status?.new || 0})</TabsTrigger>
          <TabsTrigger value="in_progress">In Progress</TabsTrigger>
          <TabsTrigger value="resolved">Resolved</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {reports.map(report => (
            <Card key={report.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedReport(report)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{report.title}</h3>
                      {report.is_anonymous && <Badge variant="outline"><EyeOff className="w-3 h-3 mr-1" />Anonymous</Badge>}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{report.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{report.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{new Date(report.date_occurred).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={getSeverityColor(report.potential_severity)}>
                      {report.potential_severity?.replace('_', ' ')}
                    </Badge>
                    <Badge className={getStatusColor(report.status)}>
                      {report.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {reports.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No near-miss reports</h3>
                <p className="text-gray-500 mb-4">Start reporting to build a safety culture</p>
                <Button onClick={() => setShowCreateDialog(true)}><Plus className="w-4 h-4 mr-2" />Report Near-Miss</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NearMissReportingPage;
